import React, { useState, useEffect, useCallback, Fragment } from "react";
import { getRequest, postRequest } from "../../utils/httpRequest";
import { colors } from "../AgenticPaymentAirdrop/styles";
import { HelpTitle } from "../AdminHeaderHelp";

// Partner exchange rates per corridor (George's Phase 6), read from lp-api.
// lp-api warns when a partner's rate is more than 3% from our mid, and pauses
// that partner for that currency over 8% until an admin resumes it. Numbers
// arrive as strings (Postgres numeric). Rows arrive sorted: paused, warned,
// widest gap first.
// Styles are inline: the admin page loads this pack's JS but not its CSS.

const card = {
  background: colors.white, border: `1px solid ${colors.border}`, borderRadius: "8px",
  padding: "20px 24px", marginBottom: "20px",
};
const note = { fontSize: "13px", color: colors.gray600, margin: "0 0 12px" };
const help = { fontSize: "12px", color: colors.gray600, marginTop: "4px" };
const label = { display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "4px" };
const input = {
  width: "100%", maxWidth: "460px", padding: "7px 10px", fontSize: "14px",
  border: `1px solid ${colors.gray400}`, borderRadius: "6px", boxSizing: "border-box",
};
const th = {
  textAlign: "left", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.4px",
  color: colors.gray600, padding: "8px 10px", borderBottom: `1px solid ${colors.border}`, whiteSpace: "nowrap",
};
const thNum = { ...th, textAlign: "right" };
const td = { padding: "8px 10px", fontSize: "13px", borderBottom: `1px solid ${colors.border}`, verticalAlign: "top" };
const tdNum = { ...td, textAlign: "right", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" };
const btn = (disabled) => ({
  background: colors.primary, color: "#fff", border: `1px solid ${colors.primary}`, borderRadius: "6px",
  padding: "8px 16px", fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.6 : 1,
});
const ghostBtn = (disabled) => ({ ...btn(disabled), background: "#fff", color: colors.primary });
const linkBtn = {
  background: "none", border: "none", padding: 0, color: colors.primary, cursor: "pointer",
  fontSize: "13px", textDecoration: "underline", marginLeft: "10px",
};
const TONES = {
  good: { bg: "#d4edda", fg: "#155724" },
  warn: { bg: "#fff3cd", fg: "#856404" },
  bad: { bg: "#f8d7da", fg: "#721c24" },
};
const Tag = ({ tone, children }) => (
  <span style={{ background: TONES[tone].bg, color: TONES[tone].fg, borderRadius: "10px", padding: "2px 8px", fontSize: "11px", fontWeight: 700, whiteSpace: "nowrap" }}>
    {children}
  </span>
);
const Banner = ({ tone, children }) => (
  <div role="status" style={{ background: TONES[tone].bg, color: TONES[tone].fg, borderRadius: "8px", padding: "12px 16px", marginBottom: "20px", fontSize: "14px" }}>
    {children}
  </div>
);

const toNum = (v) => (v == null || v === "" || !Number.isFinite(Number(v)) ? null : Number(v));
const when = (iso) => (iso ? new Date(iso).toLocaleString() : "-");
// Rates run from fractions of a cent to thousands, so keep more digits on small ones.
export const rate = (v) => {
  const n = toNum(v);
  return n == null ? "-" : n.toLocaleString("en-US", { maximumFractionDigits: Math.abs(n) >= 100 ? 2 : 6 });
};
// The partner's rate is the middle of its buy and sell.
export const partnerMid = (row) => {
  const buy = toNum(row.partnerBuy);
  const sell = toNum(row.partnerSell);
  if (buy != null && sell != null) return (buy + sell) / 2;
  return buy ?? sell;
};
const GAP_HELP = "How far the partner's rate is from our mid rate. Over 3% warns, and over 8% pauses the partner for this currency.";
export const gapText = (drift) => (toNum(drift) == null ? "-" : `${(toNum(drift) * 100).toFixed(2)}%`);
// Amber over 3%, red over 8%, the same lines lp-api warns and pauses at.
export const gapTone = (drift) => {
  const d = toNum(drift);
  if (d == null) return null;
  return d > 0.08 ? "bad" : d > 0.03 ? "warn" : "good";
};

export const STATUS = {
  ok: { tone: "good", text: "OK" },
  warn: { tone: "warn", text: "Warning" },
  paused: { tone: "bad", text: "Paused" },
};
const PARTNERS = { yellowcard: "YellowCard" };
export const partnerName = (p) => PARTNERS[String(p).toLowerCase()] || (p ? String(p).charAt(0).toUpperCase() + String(p).slice(1) : "-");

const rowKey = (r) => `${r.partner}/${r.currency}`;

const PartnerRates = ({ listUrl, historyUrl, runUrl, resumeUrl }) => {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [message, setMessage] = useState(null); // { tone, text }
  const [busy, setBusy] = useState(false);
  const [resuming, setResuming] = useState(null); // { row, reason }
  const [open, setOpen] = useState(null); // rowKey of the corridor whose history is shown
  const [history, setHistory] = useState({}); // rowKey -> { rows } | { error } | { loading }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getRequest(listUrl);
      if (res?.success && Array.isArray(res.data)) {
        setRows(res.data);
        setLoadError(null);
      } else {
        setLoadError(res?.error || "Couldn't load the partner rates.");
      }
    } catch (e) {
      setLoadError("Couldn't load the partner rates.");
    } finally {
      setLoading(false);
    }
  }, [listUrl]);

  useEffect(() => { load(); }, [load]);

  const post = async (url, body) => {
    try {
      return await postRequest(url, body);
    } catch (e) {
      return { success: false, error: e?.data?.error };
    }
  };

  const checkNow = async () => {
    setBusy(true);
    setMessage(null);
    const res = await post(runUrl, {});
    if (res?.success) {
      const d = res.data || {};
      const count = (v) => (Array.isArray(v) ? v.length : toNum(v) ?? 0);
      setMessage({
        tone: "good",
        text: `Checked ${count(d.checked)} rates: ${count(d.warnings)} with a warning, ${count(d.paused)} paused, ${count(d.skipped)} skipped.`,
      });
      await load();
    } else {
      setMessage({ tone: "bad", text: res?.error || "The check didn't run." });
    }
    setBusy(false);
  };

  const resume = async () => {
    const reason = resuming.reason.trim();
    if (!reason) return setMessage({ tone: "bad", text: "Enter a reason." });
    const { row } = resuming;
    setBusy(true);
    setMessage(null);
    const res = await post(resumeUrl, { partner: row.partner, currency: row.currency, reason });
    if (res?.success) {
      setMessage({ tone: "good", text: `${partnerName(row.partner)} is open for ${row.currency} again.` });
      setResuming(null);
      await load();
    } else {
      setMessage({ tone: "bad", text: res?.error || "That didn't save." });
    }
    setBusy(false);
  };

  const toggleHistory = async (row) => {
    const key = rowKey(row);
    if (open === key) return setOpen(null);
    setOpen(key);
    setHistory((h) => ({ ...h, [key]: { loading: true } }));
    const query = `partner=${encodeURIComponent(row.partner)}&currency=${encodeURIComponent(row.currency)}`;
    try {
      const res = await getRequest(`${historyUrl}?${query}`);
      setHistory((h) => ({ ...h, [key]: res?.success && Array.isArray(res.data) ? { rows: res.data } : { error: res?.error || "Couldn't load the history." } }));
    } catch (e) {
      setHistory((h) => ({ ...h, [key]: { error: "Couldn't load the history." } }));
    }
  };

  const header = (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px", flexWrap: "wrap", marginBottom: "20px" }}>
      <p style={{ ...note, margin: 0, maxWidth: "760px" }}>
        Where each partner converts, next to our rate. More than 3% apart sends a warning. More than 8% pauses that partner for that currency until someone checks the rate and resumes it.
      </p>
      <div style={{ display: "flex", gap: "8px" }}>
        <button type="button" style={ghostBtn(loading)} disabled={loading} onClick={load}>{loading ? "Refreshing…" : "Refresh"}</button>
        <button type="button" style={btn(busy)} disabled={busy} onClick={checkNow}>{busy ? "Checking…" : "Check now"}</button>
      </div>
    </div>
  );

  if (!rows) {
    return (
      <div>
        {header}
        {message && <Banner tone={message.tone}>{message.text}</Banner>}
        {loading ? <p style={note}>Loading…</p> : <Banner tone="bad">{loadError} Refresh to try again.</Banner>}
      </div>
    );
  }

  const historyPanel = (row) => {
    const h = history[rowKey(row)] || {};
    if (h.loading) return <p style={{ ...note, margin: 0 }}>Loading…</p>;
    if (h.error) return <p style={{ ...note, margin: 0, color: TONES.bad.fg }}>{h.error}</p>;
    if (!h.rows?.length) return <p style={{ ...note, margin: 0 }}>No readings yet.</p>;
    return (
      <div style={{ maxHeight: "320px", overflowY: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", background: colors.white }}>
          <thead>
            <tr><th style={th}>When</th><th style={thNum}>Partner buy</th><th style={thNum}>Partner sell</th><th style={thNum}>Our rate</th><th style={thNum}><HelpTitle title="Gap" help={GAP_HELP} /></th></tr>
          </thead>
          <tbody>
            {h.rows.map((s, i) => (
              <tr key={`${s.at}-${i}`}>
                <td style={{ ...td, whiteSpace: "nowrap" }}>{when(s.at)}</td>
                <td style={tdNum}>{rate(s.partnerBuy)}</td>
                <td style={tdNum}>{rate(s.partnerSell)}</td>
                <td style={tdNum}>{rate(s.ourMid)}</td>
                <td style={tdNum}>{gapTone(s.drift) ? <Tag tone={gapTone(s.drift)}>{gapText(s.drift)}</Tag> : "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div>
      {header}
      {loadError && <Banner tone="bad">{loadError} Refresh to try again.</Banner>}
      {message && <Banner tone={message.tone}>{message.text}</Banner>}

      <div style={card}>
        {rows.length === 0 ? <p style={{ ...note, margin: 0 }}>No partner rates yet. Check now to read them.</p> : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={th}>Partner</th><th style={th}>Currency</th><th style={thNum}>Partner rate</th><th style={thNum}>Our rate</th>
                  <th style={thNum}><HelpTitle title="Gap" help={GAP_HELP} /></th><th style={th}>Status</th><th style={th}>Checked</th><th style={th} />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const key = rowKey(r);
                  const status = STATUS[r.status] || { tone: "warn", text: String(r.status || "-") };
                  const tone = gapTone(r.drift);
                  return (
                    <Fragment key={key}>
                      <tr>
                        <td style={td}>{partnerName(r.partner)}</td>
                        <td style={td}><strong>{r.currency}</strong></td>
                        <td style={tdNum}>
                          {rate(partnerMid(r))}
                          <div style={help}>Buy {rate(r.partnerBuy)} · Sell {rate(r.partnerSell)}</div>
                        </td>
                        <td style={tdNum}>{rate(r.ourMid)}</td>
                        <td style={tdNum}>{tone ? <Tag tone={tone}>{gapText(r.drift)}</Tag> : "-"}</td>
                        <td style={td}>
                          <Tag tone={status.tone}>{status.text}</Tag>
                          {r.status === "paused" && (r.pausedReason || r.pausedAt) && (
                            <div style={help}>{r.pausedReason ? `${r.pausedReason}. ` : ""}Since {when(r.pausedAt)}.</div>
                          )}
                          {r.resumedAt && (
                            <div style={help}>Resumed by {r.resumedBy || "unknown"} on {when(r.resumedAt)}{r.resumeReason ? `: ${r.resumeReason}` : ""}</div>
                          )}
                        </td>
                        <td style={{ ...td, whiteSpace: "nowrap" }}>{when(r.checkedAt)}</td>
                        <td style={{ ...td, whiteSpace: "nowrap" }}>
                          {r.status === "paused" && (
                            <button type="button" style={{ ...linkBtn, marginLeft: 0 }} onClick={() => { setResuming({ row: r, reason: "" }); setMessage(null); }}>Resume</button>
                          )}
                          <button type="button" style={r.status === "paused" ? linkBtn : { ...linkBtn, marginLeft: 0 }} aria-expanded={open === key} onClick={() => toggleHistory(r)}>
                            {open === key ? "Hide history" : "History"}
                          </button>
                        </td>
                      </tr>
                      {open === key && (
                        <tr>
                          <td colSpan={8} style={{ ...td, background: colors.gray100 }}>
                            <div style={{ fontSize: "13px", fontWeight: 600, marginBottom: "8px" }}>
                              {partnerName(r.partner)} {r.currency}, hourly readings for the last week
                            </div>
                            {historyPanel(r)}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p style={{ ...note, margin: "12px 0 0" }}>Only YellowCard publishes its rates today, so it is the only partner here.</p>

        {resuming && (
          <div style={{ marginTop: "16px", padding: "16px", border: `1px solid ${colors.border}`, borderRadius: "8px", background: colors.gray100 }}>
            <p style={{ margin: "0 0 12px", fontSize: "14px" }}>
              Resume {partnerName(resuming.row.partner)} for {resuming.row.currency}? Check their rate first. Payments in {resuming.row.currency} go through them again right away.
            </p>
            <div style={{ marginBottom: "12px" }}>
              <label style={label} htmlFor="resume-reason">Reason</label>
              <input id="resume-reason" type="text" maxLength={500} style={input}
                value={resuming.reason} onChange={(e) => setResuming({ ...resuming, reason: e.target.value })} />
              <div style={help}>Saved with your email.</div>
            </div>
            <button type="button" style={btn(busy)} disabled={busy} onClick={resume}>Resume</button>
            <button type="button" style={{ ...ghostBtn(busy), marginLeft: "8px" }} disabled={busy} onClick={() => setResuming(null)}>Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PartnerRates;
